/**
 * API tokens service
 */

import { apiEndpoints } from "@/src/config/api/endpoints";

import { api } from "./base_service";
import type { ApiError, Paginated, Result } from "./types";

const TOKENS_API_VERSION = "3.0";

/** A `/tokens/` entry as the backend sends it (snake_case). */
export interface ApiTokenRaw {
  key: string;
  name?: string | null;
  created_at?: string | null;
  is_sandbox: boolean;
  is_default: boolean;
  is_deleted?: boolean;
  timestamp?: string | null;
}

/** An API token at the camelCase boundary. */
export interface ApiToken {
  /** Full, unmasked key — returned on both create and list. */
  key: string;
  name: string;
  createdAt: string;
  /** Sandbox (test) vs production token. */
  isSandbox: boolean;
  /** Only production tokens can be default; sandbox tokens cannot. */
  isDefault: boolean;
  isDeleted: boolean;
  /** Last-modified timestamp (e.g. set-default, rename). */
  timestamp: string;
}

export function adaptApiToken(raw: ApiTokenRaw): ApiToken {
  return {
    key: raw.key,
    name: raw.name ?? "",
    createdAt: raw.created_at ?? "",
    isSandbox: raw.is_sandbox,
    isDefault: raw.is_default,
    isDeleted: !!raw.is_deleted,
    timestamp: raw.timestamp ?? "",
  };
}

export async function listApiTokens(): Promise<Result<ApiToken[]>> {
  try {
    const data = await api.get<Paginated<ApiTokenRaw> | ApiTokenRaw[]>(
      apiEndpoints.apiTokens().list(),
      { version: TOKENS_API_VERSION },
    );
    const list = Array.isArray(data) ? data : (data.results ?? []);
    return { ok: true, value: list.map(adaptApiToken) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function createApiToken(isSandbox: boolean, name?: string): Promise<Result<ApiToken>> {
  try {
    const data = await api.post<ApiTokenRaw>(
      apiEndpoints.apiTokens().create(),
      {
        is_sandbox: isSandbox,
        ...(name ? { name } : {}),
      },
      { version: TOKENS_API_VERSION },
    );
    return { ok: true, value: adaptApiToken(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function deleteApiToken(key: string): Promise<Result<void>> {
  try {
    await api.delete<void>(apiEndpoints.apiTokens().delete(key));
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function setDefaultApiToken(key: string): Promise<Result<ApiToken>> {
  try {
    const data = await api.patch<ApiTokenRaw>(
      apiEndpoints.apiTokens().setDefault(key),
      { is_default: true },
      { version: TOKENS_API_VERSION },
    );
    return { ok: true, value: adaptApiToken(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}
